import { useState } from "react";
import { api } from "../lib/api.js";
import { Mostarda } from "../components/Mostarda.js";

/**
 * Check-in matinal — dois toques.
 *
 * Existe porque nenhum dos dois relógios entrega HRV: o app Zepp nunca exportou
 * variabilidade para o Apple Health, e o lado Samsung é disputado. Sem HRV, a
 * percepção da própria pessoa é o melhor sinal de recuperação disponível, e é
 * o que o Bearable usa.
 *
 * Por isso a tela é deliberadamente minúscula. Se custar mais que cinco
 * segundos, ninguém faz, e um componente que ninguém preenche é pior que um
 * componente que não existe — ele deixa a baseline cheia de buracos.
 */

const ENERGIA = [
  { valor: 1, rotulo: "Acabado" },
  { valor: 2, rotulo: "Devagar" },
  { valor: 3, rotulo: "Normal" },
  { valor: 4, rotulo: "Bem" },
  { valor: 5, rotulo: "Ligado" },
];

const DOR = [
  { valor: 1, rotulo: "Zero" },
  { valor: 2, rotulo: "Leve" },
  { valor: 3, rotulo: "Média" },
  { valor: 4, rotulo: "Bastante" },
  { valor: 5, rotulo: "Travado" },
];

export function CheckIn({ aoFechar }: { aoFechar: () => void }) {
  const [energia, setEnergia] = useState<number | null>(null);
  const [dor, setDor] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const completo = energia !== null && dor !== null;

  async function salvar() {
    if (!completo) return;
    setOcupado(true);
    setErro(null);
    try {
      // `mood` reaproveita o endpoint que já existe. O humor geral é derivado
      // da energia quando a pessoa não informa separadamente — não inventamos
      // um terceiro toque para uma métrica que ninguém pediu.
      await api.post("/log/mood", { mood: energia, energy: energia, soreness: dor });
      aoFechar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui salvar.");
      setOcupado(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-6 px-5">
      <div className="flex flex-col items-center gap-2 text-center">
        <Mostarda estado="espreitando" tamanho={110} />
        <h1 className="text-xl font-bold">Como tu acordou?</h1>
        <p className="text-xs text-suave">
          Dois toques. Sem HRV no teu relógio, é isto que mais sustenta o teu score do dia.
        </p>
      </div>

      <Escala rotulo="Energia" opcoes={ENERGIA} valor={energia} aoEscolher={setEnergia} />
      <Escala rotulo="Dor muscular" opcoes={DOR} valor={dor} aoEscolher={setDor} />

      {erro && <p role="alert" className="text-sm text-alerta">{erro}</p>}

      <div className="flex flex-col gap-2">
        <button className="botao-primario" disabled={!completo || ocupado} onClick={salvar}>
          {ocupado ? "Salvando…" : "Pronto"}
        </button>
        <button className="botao-secundario" onClick={aoFechar}>
          Hoje não
        </button>
      </div>
    </main>
  );
}

function Escala({
  rotulo,
  opcoes,
  valor,
  aoEscolher,
}: {
  rotulo: string;
  opcoes: { valor: number; rotulo: string }[];
  valor: number | null;
  aoEscolher: (v: number) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="rotulo mb-1">{rotulo}</legend>
      <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label={rotulo}>
        {opcoes.map((o) => {
          const ativo = valor === o.valor;
          return (
            <button
              key={o.valor}
              role="radio"
              aria-checked={ativo}
              onClick={() => aoEscolher(o.valor)}
              className={`flex min-h-[62px] flex-col items-center justify-center gap-0.5 rounded-xl2 border px-1 py-2 transition ${
                ativo
                  ? "border-mostarda bg-mostarda font-bold text-[rgb(33_27_21)]"
                  : "border-borda bg-superficie"
              }`}
            >
              <span className="text-base font-bold tabular-nums">{o.valor}</span>
              <span className="text-[10px] leading-tight">{o.rotulo}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
